import { useCallback, useEffect, useMemo, useState } from 'react';
import FeatureFlagsService, { FeatureFlag } from './feature-flags.service';
import { ExportedFeatureFlag, FeatureFlagsHashMap } from './types';

export interface FeatureFlagsOptions {
    serverUrl: string;
    appName: string;
}

export function useFeatureFlags(options: FeatureFlagsOptions) {
    const { serverUrl, appName } = options;
    const [data, setData] = useState<FeatureFlagsHashMap>({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const featureFlags = new FeatureFlagsService(serverUrl);

        featureFlags.getBy(appName)
            .then(response => response?.map(toExportedFeatureFlag) || [])
            .then(list => list.reduce(toFeatureFlagsHashMap, {}))
            .then(map => setData(map))
            .catch(err => setError(err))
            .finally(() => setIsLoading(false));
    }, []);

    const state = useMemo(() => ({ data, error, loading: isLoading }), [data, error, isLoading]);
    const isEnabled = useCallback(
        (flagName: string) => {
            if (isLoading || error) return false;
            if (data[flagName] === undefined) return false;
            return data[flagName]?.isEnabled || false;
        },
        [data, error, isLoading],
    );

  return [state, isEnabled] as const;
}

export default useFeatureFlags;

function toExportedFeatureFlag(featureFlag: FeatureFlag): ExportedFeatureFlag {
    return {
        featureName: featureFlag.name,
        isEnabled: featureFlag.enabled,
    };
}
function toFeatureFlagsHashMap(map: FeatureFlagsHashMap = {}, exported: ExportedFeatureFlag) {
    return {
        ...map,
        [exported.featureName]: exported,
    };
}
